var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingButton } from "@mui/lab";
import { Button, Divider, Stack } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useAllSectors } from "../../../utils/hooks/sectors/useSectors";
import { getSectorIcon } from "../../sectorIcons/sectorIcons";
import { buildVisits, byNewestEnd, formatVisitDates, hasEnded, isDuring } from "./visits";
import { GroupTitle, useDateColumnWidth, VisitRow } from "./VisitRow";
import { PROFILE_VISITS_PAGE_SIZE, profilePastVisitsQuery } from "./profilePastVisits.query";
var ROWS_STEP = 5;
var footerButtonSx = {
    minWidth: 0,
    height: 24,
    p: 0,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
};
// Profile-wide history includes every sector and all past statuses.
var ProfilePastVisits = function (_a) {
    var _b;
    var profile = _a.profile;
    var t = useTranslation().t;
    var _c = useState(ROWS_STEP), shown = _c[0], setShown = _c[1];
    var _d = useState(PROFILE_VISITS_PAGE_SIZE), limit = _d[0], setLimit = _d[1];
    var _e = useState(), upcomingOpen = _e[0], setUpcomingOpen = _e[1];
    var _f = useState(true), stayOpen = _f[0], setStayOpen = _f[1];
    var _g = useState(), otherOpen = _g[0], setOtherOpen = _g[1];
    var visitsQuery = useQuery(profilePastVisitsQuery(profile.id, limit));
    var page = visitsQuery.data;
    var sectors = useAllSectors();
    var sectorIconOf = function (visit) { var _a, _b; return getSectorIcon((_a = sectors.find(function (sector) { var _a; return sector.id === ((_a = visit.sector) === null || _a === void 0 ? void 0 : _a.id); })) !== null && _a !== void 0 ? _a : { sectorType: (_b = visit.sector) === null || _b === void 0 ? void 0 : _b.sectorType }); };
    var hasMore = !!page && page.content.length < page.totalElements;
    var reservations = ((_b = page === null || page === void 0 ? void 0 : page.content) !== null && _b !== void 0 ? _b : []).filter(function (reservation) { return !!reservation.startDate; });
    var stay = profile.currentStay;
    var duringStay = stay ? reservations.filter(function (reservation) { return isDuring(reservation, stay); }) : [];
    var outside = reservations.filter(function (reservation) { return !duringStay.includes(reservation); });
    var upcoming = buildVisits(outside.filter(function (reservation) { return !hasEnded(reservation); }), t, false).sort(byNewestEnd);
    var stayVisits = buildVisits(duringStay, t, false).sort(byNewestEnd);
    var past = buildVisits(outside.filter(hasEnded), t, false).sort(byNewestEnd);
    // Measure all loaded rows so expanding a group does not shift its columns.
    var upcomingWidth = useDateColumnWidth(upcoming);
    var stayWidth = useDateColumnWidth(stayVisits);
    var pastWidth = useDateColumnWidth(past);
    var rowsOf = function (visits, dateWidth) { return visits.map(function (visit) {
        return _jsx(VisitRow, { visit: visit, dateWidth: dateWidth, sectorIcon: sectorIconOf(visit) }, visit.id);
    }); };
    if (upcoming.length + stayVisits.length + past.length === 0) {
        return null;
    }
    var hasStay = !!stay && stayVisits.length > 0;
    var isUpcomingOpen = upcomingOpen !== null && upcomingOpen !== void 0 ? upcomingOpen : !hasStay;
    var isOtherOpen = otherOpen !== null && otherOpen !== void 0 ? otherOpen : !hasStay;
    var canLoadMore = shown < past.length || hasMore;
    var loadMore = function () {
        var next = shown + ROWS_STEP;
        setShown(next);
        if (past.length < next && hasMore) {
            setLimit(limit + PROFILE_VISITS_PAGE_SIZE);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Stack, __assign({ gap: '10px' }, { children: [_jsxs(Stack, __assign({ gap: '12px' }, { children: [upcoming.length > 0 && (_jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(GroupTitle, __assign({ folded: !isUpcomingOpen, onToggle: function () { return setUpcomingOpen(!isUpcomingOpen); } }, { children: t('Upcoming') })), isUpcomingOpen && rowsOf(upcoming, upcomingWidth)] }))), stay && hasStay && (_jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(GroupTitle, __assign({ current: true, folded: !stayOpen, onToggle: function () { return setStayOpen(!stayOpen); } }, { children: "".concat(t('This stay'), " \u00B7 ").concat(formatVisitDates(DateTime.fromISO(stay.startDay), DateTime.fromISO(stay.endDay))) })), stayOpen && rowsOf(stayVisits, stayWidth)] }))), past.length > 0 && (_jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(GroupTitle, __assign({ folded: !isOtherOpen, onToggle: function () { return setOtherOpen(!isOtherOpen); } }, { children: t('Other') })), isOtherOpen && rowsOf(past.slice(0, shown), pastWidth)] })))] })), isOtherOpen && (canLoadMore || shown > ROWS_STEP) && (_jsxs(Stack, __assign({ direction: 'row', gap: '18px' }, { children: [canLoadMore && (_jsx(LoadingButton, __assign({ loading: visitsQuery.isPreviousData && visitsQuery.isFetching, onClick: loadMore, sx: footerButtonSx }, { children: t('Load more') }))), shown > ROWS_STEP && (_jsx(Button, __assign({ onClick: function () { return setShown(ROWS_STEP); }, sx: footerButtonSx }, { children: t('Show less') })))] })))] }))] }));
};
export default ProfilePastVisits;
